// @ts-nocheck
import fieldDefinitionBuilder from "@store/Framework/Features/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { FieldDefinitionBuilderOptions } from "@store/Framework/Features/FieldDefinitionBuilder/FieldDefinitionBuilderTypes";
import { responsiveCss } from "@store/Framework/Features/FieldDefinitionBuilder/Utilities/responsiveCss";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import { BorderFieldOptions } from "@store/Overrides/Widgets/Basic/PageTitle";
import { lazyImageStyles } from "@store/Themes/common/mixins/lazyload";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { css } from "styled-components";

const enum fields {
    imagePath = "imagePath",
    title = "title",

    hideBreadcrumbs = "hideBreadcrumbs",
    titleColor = "titleColor",
    hasCustomOverlay = "hasCustomOverlay",
    customOverlayImage = "customOverlayImage",
    customOverlayPosition = "customOverlayPosition",
    customOverlayAligment = "customOverlayAligment",
    overlay = "overlay",
    colorOverlay = "colorOverlay",
    aligment = "aligment",
}

interface Props extends WidgetProps {
    fields: {
        [fields.imagePath]: string;
        [fields.title]: string;

        [fields.hideBreadcrumbs]: boolean;
        [fields.titleColor]: string;
        [fields.hasCustomOverlay]: boolean;
        [fields.customOverlayImage]: string;
        [fields.customOverlayPosition]: number;
        [fields.customOverlayAligment]: "left" | "right";
        [fields.overlay]: boolean;
        [fields.colorOverlay]: string;
        [fields.aligment]: "flex-start" | "center" | "flexp-end";
    } & FieldDefinitionBuilderOptions<"titleType" | "titleStyle"> &
        BorderFieldOptions;
}

export interface FormHeaderStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    image?: LazyImageProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    customOverlayImage?: LazyImageProps;
}

export const formHeaderStyles: FormHeaderStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.accentLighter")};
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
            display: flex;
            width: 100%;
            height: ${pxToRem(350)};
            border-bottom: ${pxToRem(8)} solid ${getColor("secondary")};

            ${breakpoint("sm", "down")} {
                height: 32vh;
            }
        `,
    },
    overlayWrapper: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;
        `,
    },
    image: {
        css: css`
            position: relative;
            width: 100%;
            ${lazyImageStyles()}

            img {
                position: absolute;
                top: 50%;
                width: auto;
                min-width: 100%;
                min-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            ${siteWidthStyles()}
        `,
    },
    contentWrapper: {
        css: css`
            ${siteWidthStyles()}
            padding-top: 1.75em;
            padding-bottom: 2.75em;
            text-align: center;

            ${breakpoint("sm")} {
                padding-top: 0.75em;
                padding-bottom: 1.75em;
            }

            body {
                background-color: transparent;
            }
        `,
    },
    textWrapper: {
        css: css`
            max-width: ${pxToRem(930)};
            margin: 0 auto;
        `,
    },
    customOverlayImage: {
        css: css`
            position: absolute;
            height: 50vh;

            img {
                height: 120%;
            }
        `,
    },
};

const styles = formHeaderStyles;

const FormHeader: React.FC<Props> = ({ fields }) => {
    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}

            ${responsiveCss(fields, field => {
                return css`
                    ${field.hasCustomOverlay &&
                    `
                    justify-content: ${field.customOverlayAligment === "right" ? "flex-end" : "flex-start"};
                `}
                    background-color: ${field.overlay && !field.hasCustomOverlay ? field.colorOverlay : "transparent"};
                `;
            })}
            overflow: hidden;
        `,
    };

    const customOverlayImageStyles: LazyImageProps = {
        ...styles.customOverlayImage,
        css: css`
            ${styles.customOverlayImage?.css}

            ${responsiveCss(fields, field => {
                return css`
                    ${field.customOverlayAligment === "right"
                        ? `
                        transform: rotate(180deg) translateX(${field.customOverlayPosition}%);   
                    `
                        : `
                    transform: translateX(-${field.customOverlayPosition}%); 
                    
                `}
                    ${field.hasCustomOverlay && !!field.customOverlayImage
                        ? `
                    display: inline-flex;
                `
                        : `
                    display: none;
                `}
                `;
            })}
        `,
    };

    const titleWrapperStyles = {
        css: css`
            ${styles.titleWrapper?.css}

            ${responsiveCss(
                fields,
                field => css`
                    justify-content: ${field.aligment};
                    z-index: 1;

                    > * {
                        color: ${field.titleColor} !important;
                    }
                `,
            )}
        `,
    };

    const textProps: TypographyPresentationProps = {
        variant: fields.titleStyle,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={fields.imagePath} {...styles.image} />
                <StyledWrapper {...overlayWrapperStyles}>
                    <StyledWrapper {...titleWrapperStyles}>
                        <Typography as={fields.titleType} {...textProps} data-test-selector="PageTitle">
                            {fields.title}
                        </Typography>
                    </StyledWrapper>
                    {fields.customOverlayImage && (
                        <LazyImage src={fields.customOverlayImage} {...customOverlayImageStyles} />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FoundationFieldDefinition> = {
    component: FormHeader,
    definition: {
        group: "Basic",
        displayName: "Form Header",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.imagePath,
                displayName: "Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            ...fieldDefinitionBuilder(
                [
                    {
                        fieldType: "General",
                        name: fields.titleColor,
                        displayName: "Title Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },

                    {
                        name: fields.hasCustomOverlay,
                        displayName: "Apply a Custom Overlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        fieldType: "General",
                    },
                    {
                        name: fields.customOverlayImage,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        fieldType: "General",
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.customOverlayPosition,
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                        tooltip: "The position is apllied in percentage, not in pixels",
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.customOverlayAligment,
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "left",
                        hideEmptyOption: false,
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        name: fields.overlay,
                        displayName: "Add an Overlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        fieldType: "General",
                        isVisible: widget => !widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.colorOverlay,
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.overlay && !widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.aligment,
                        displayName: "Title Aligment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                ],
                {
                    typographyOptions: {
                        titleStyle: true,
                        titleType: true,
                    },
                },
            ),
        ],
    },
};

export default widgetModule;
